import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { auth, db } from '../firebase';

const REQUIRED_COLUMNS = ['name','mobile','state','plan_type','carrier','plan','premium','start_date'];

const VALID_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const styles = {
  screen: { minHeight: '100vh', backgroundColor: '#F4F6F9', fontFamily: 'system-ui, sans-serif' },
  main: { marginLeft: 220, padding: 24 },
  pageTitle: { fontSize: 22, fontWeight: '700', color: '#0A2D5A', marginBottom: 4 },
  pageSub: { fontSize: 13, color: '#888', marginBottom: 24 },
  card: { backgroundColor: 'white', borderRadius: 12, border: '0.5px solid #e0e0e0', padding: 24, marginBottom: 24 },
  sectionTitle: { fontSize: 15, fontWeight: '700', color: '#0A2D5A', marginBottom: 8 },
  sectionSub: { fontSize: 13, color: '#666', marginBottom: 16 },
  downloadBtn: { backgroundColor: '#1D6FA4', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: '600', cursor: 'pointer' },
  uploadArea: { border: '2px dashed #ddd', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer', color: '#888', fontSize: 14 },
  uploadAreaActive: { border: '2px dashed #1D6FA4', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer', color: '#1D6FA4', fontSize: 14, backgroundColor: '#E6F1F9' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { backgroundColor: '#F4F6F9', padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: '#0A2D5A', borderBottom: '1px solid #e0e0e0' },
  td: { padding: '8px 10px', borderBottom: '0.5px solid #f0f0f0', color: '#333' },
  tdError: { padding: '8px 10px', borderBottom: '0.5px solid #f0f0f0', color: '#CC0000', backgroundColor: '#FEF2F2' },
  errorBadge: { backgroundColor: '#FEE2E2', color: '#CC0000', fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: '600' },
  validBadge: { backgroundColor: '#DCFCE7', color: '#166534', fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: '600' },
  importBtn: { backgroundColor: '#2E7D32', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: '600', cursor: 'pointer' },
  importBtnDisabled: { backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: '600', cursor: 'not-allowed' },
  cancelBtn: { backgroundColor: 'white', color: '#666', border: '1px solid #ddd', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: '600', cursor: 'pointer', marginRight: 12 },
  progressBar: { height: 6, backgroundColor: '#e0e0e0', borderRadius: 3, marginTop: 12 },
  progressFill: (pct) => ({ height: 6, backgroundColor: '#2E7D32', borderRadius: 3, width: `${pct}%`, transition: 'width 0.3s' }),
};

function normalizeDate(val) {
  if (!val) return '';
  const trimmed = val.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const [m, d, yr] = trimmed.split('/');
    return `${yr}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
  }
  return trimmed;
}

function parseBirthday(val) {
  if (!val) return { birthdayMonth: '', birthdayDay: '' };
  const trimmed = val.trim();
  if (/^\d{1,2}\/\d{1,2}/.test(trimmed)) {
    const parts = trimmed.split('/');
    return {
      birthdayMonth: parseInt(parts[0], 10).toString(),
      birthdayDay: parseInt(parts[1], 10).toString(),
    };
  }
  if (/^\d{1,2}-\d{1,2}$/.test(trimmed)) {
    const parts = trimmed.split('-');
    return {
      birthdayMonth: parseInt(parts[0], 10).toString(),
      birthdayDay: parseInt(parts[1], 10).toString(),
    };
  }
  return { birthdayMonth: '', birthdayDay: '' };
}

function parseReviewInterval(val) {
  if (!val) return '1';
  const trimmed = val.trim();
  if (['1','2','3'].includes(trimmed)) return trimmed;
  return '1';
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
  const rows = lines.slice(1).map((line, i) => {
    const values = line.split(',').map(v => v.trim());
    const row = {};
    headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
    row._rowNum = i + 2;
    return row;
  });
  return { headers, rows };
}

function validateRow(row) {
  const errors = [];
  if (!row.name) errors.push('Name required');
  if (!row.mobile || !/^\d{10}$/.test(row.mobile.replace(/\D/g, ''))) errors.push('Mobile must be 10 digits');
  if (!row.state || !VALID_STATES.includes(row.state.toUpperCase())) errors.push('Invalid state');
  if (!row.plan_type || !['supplement','advantage'].includes(row.plan_type.toLowerCase())) errors.push('Plan type must be Supplement or Advantage');
  if (!row.carrier) errors.push('Carrier required');
  if (!row.start_date) errors.push('Start date required');
  if (row.review_interval && !['1','2','3'].includes(row.review_interval.trim())) errors.push('Review interval must be 1, 2, or 3');
  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim())) errors.push('Email format is invalid');
  return errors;
}

function downloadTemplate() {
  const headers = 'name,mobile,email,state,plan_type,carrier,plan,premium,start_date,birthday,review_interval';
  const blob = new Blob([headers + '\n'], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'medicare-plan-guardian-import-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function ImportClients() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [missingCols, setMissingCols] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [importedCount, setImportedCount] = useState(0);

  const agentId = auth.currentUser?.uid;

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      const missing = REQUIRED_COLUMNS.filter(c => !headers.includes(c));
      setMissingCols(missing);
      const validated = rows.map(row => ({ ...row, _errors: validateRow(row) }));
      setRows(validated);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.csv')) handleFile(file);
  };

  const handleFileInput = (e) => {
    handleFile(e.target.files[0]);
  };

  const validRows = rows.filter(r => r._errors.length === 0);
  const errorRows = rows.filter(r => r._errors.length > 0);

  const handleImport = async () => {
    if (!agentId || validRows.length === 0) return;
    setImporting(true);
    let count = 0;
    for (const row of validRows) {
      const startDate = normalizeDate(row.start_date);
      const { birthdayMonth, birthdayDay } = parseBirthday(row.birthday);
      const reviewInterval = parseReviewInterval(row.review_interval);
      await addDoc(collection(db, 'clients'), {
        agentId,
        name: row.name.trim(),
        mobile: row.mobile.replace(/\D/g, ''),
        email: (row.email || '').trim(),
        linkEmailedAt: null,
        state: row.state.toUpperCase().trim(),
        planType: row.plan_type.charAt(0).toUpperCase() + row.plan_type.slice(1).toLowerCase(),
        carrier: row.carrier.trim(),
        plan: row.plan || '',
        premium: row.premium || '',
        startDate,
        birthdayMonth,
        birthdayDay,
        reviewInterval,
        aepStatus: null,
        createdAt: serverTimestamp(),
      });
      count++;
      setProgress(Math.round((count / validRows.length) * 100));
    }
    setImportedCount(count);
    setImporting(false);
    setDone(true);
  };

  if (done) {
    return (
      <div style={styles.screen}>
        <Sidebar />
        <div style={styles.main}>
          <div style={{ ...styles.card, textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 22, fontWeight: '700', color: '#0A2D5A', marginBottom: 8 }}>
              {importedCount} Clients Imported
            </div>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 24 }}>
              All clients have been added to your portal and their app links are ready to send.
            </div>
            <button style={styles.importBtn} onClick={() => navigate('/clients')}>
              Go to My Clients
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <Sidebar />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Import Clients</div>
        <div style={styles.pageSub}>Upload a CSV file to add multiple clients at once.</div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>Step 1 — Download the Template</div>
          <div style={styles.sectionSub}>
            Fill in your client information using this format. Do not change the column headers.
            Enter dates as MM/DD/YYYY. Birthday is month and day only — MM/DD.
            For Supplement clients, enter 1, 2, or 3 in the review_interval column. Leave blank to default to 1 year.
            Email is optional, but clients without an email on file cannot be included in the bulk email delivery feature.
          </div>
          <button style={styles.downloadBtn} onClick={downloadTemplate}>
            Download CSV Template
          </button>
        </div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>Step 2 — Upload Your File</div>
          <div style={styles.sectionSub}>Drag and drop your completed CSV file below, or click to browse.</div>
          <div
            style={dragOver ? styles.uploadAreaActive : styles.uploadArea}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('csvInput').click()}
          >
            {fileName ? fileName : 'Drop your CSV here or click to browse'}
            <input id="csvInput" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileInput} />
          </div>
          {missingCols.length > 0 && (
            <div style={{ marginTop: 12, padding: 12, backgroundColor: '#FEF2F2', borderRadius: 8, color: '#CC0000', fontSize: 13 }}>
              Missing required columns: {missingCols.join(', ')}. Please use the template above.
            </div>
          )}
        </div>

        {rows.length > 0 && missingCols.length === 0 && (
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={styles.sectionTitle}>Step 3 — Review & Import</div>
                <div style={styles.sectionSub}>
                  {validRows.length} valid · {errorRows.length} with errors (errors will be skipped)
                </div>
              </div>
              <div>
                <button style={styles.cancelBtn} onClick={() => { setRows([]); setFileName(''); }}>
                  Clear
                </button>
                <button
                  style={validRows.length > 0 && !importing ? styles.importBtn : styles.importBtnDisabled}
                  onClick={handleImport}
                  disabled={validRows.length === 0 || importing}
                >
                  {importing ? `Importing... ${progress}%` : `Import ${validRows.length} Clients`}
                </button>
              </div>
            </div>
            {importing && (
              <div style={styles.progressBar}>
                <div style={styles.progressFill(progress)} />
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Row</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Mobile</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>State</th>
                    <th style={styles.th}>Plan Type</th>
                    <th style={styles.th}>Carrier</th>
                    <th style={styles.th}>Start Date</th>
                    <th style={styles.th}>Review Interval</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>{row._rowNum}</td>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>{row.name}</td>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>{row.mobile}</td>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>{row.email || '—'}</td>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>{row.state}</td>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>{row.plan_type}</td>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>{row.carrier}</td>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>{row.start_date}</td>
                      <td style={row._errors.length > 0 ? styles.tdError : styles.td}>
                        {row.review_interval || '1'}
                      </td>
                      <td style={styles.td}>
                        {row._errors.length > 0
                          ? <span style={styles.errorBadge}>{row._errors[0]}</span>
                          : <span style={styles.validBadge}>Ready</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

